import { type BaseVirtualFolder } from '@modules/common/types/base-virtual-folder';

/**
 * Whether each virtual folder that every app shares can hold a newly created item.
 *
 * Declared as a partial string record so an app's own folder names can be looked up against it, but
 * checked with `satisfies` so adding a member to {@link BaseVirtualFolder} is a compile error until it
 * gets an explicit decision here — defaulting a new folder silently would reintroduce DP-001.
 */
const BASE_VIRTUAL_FOLDER_ALLOWS_CREATION: Readonly<Partial<Record<string, boolean>>> = {
  // Triage states and bins. A new item carries none of them, so it would not appear in the list.
  flagged: false,
  followed: false,
  snoozed: false,
  archived: false,
  deleted: false,
  // A derived page like the others: a new item carries no relation to the search terms that produced
  // this list, so it would not appear in it either.
  search_results: false,
} satisfies Record<BaseVirtualFolder, boolean>;

/**
 * Whether an app page should offer a creation ("Add New") form.
 *
 * The dynamic panel's quick form does not seed the page's own attribute the way the main-view sidebar
 * does (see the apps' `getCommonParams`), so on a derived page it would create an item that does not
 * match the list the user is looking at. Hiding it there is UX audit DP-001.
 *
 * `appNonCreatableFolders` adds the derived pages specific to one app — tasking's due-date and
 * priority buckets, contacts' VIP. Unknown folder names are treated as creatable.
 */
export function isCreationAllowedInVirtualFolder<T extends string>(
  folder: T | undefined,
  appNonCreatableFolders?: ReadonlySet<T>,
): boolean {
  if (!folder) {
    return true;
  }

  if (appNonCreatableFolders?.has(folder)) {
    return false;
  }

  // `hasOwn`, not a bare lookup: a folder named `constructor` or `toString` would otherwise answer with
  // that member of `Object.prototype`. Every one of them is truthy, so the decision came out the same —
  // what was wrong is the value, a function where this returns `boolean`.
  return Object.hasOwn(BASE_VIRTUAL_FOLDER_ALLOWS_CREATION, folder)
    ? (BASE_VIRTUAL_FOLDER_ALLOWS_CREATION[folder] ?? true)
    : true;
}
